import { createFileRoute } from "@tanstack/react-router";

import Chat from "@/components/Chat";
import { RequireAuth } from "@/components/route-guards";
import { useAuth } from "@/lib/auth";
import { signOut } from "@/lib/platform-fetch";

/**
 * `/` -- the chat page. Replaces `src/app/page.tsx`.
 *
 * **The three `initial*` props are deliberately omitted, and that is a real trade.** The Next
 * page prefetched sessions, model aliases and an AppSync subscription ticket server-side and
 * passed them down, specifically to avoid a fetch-on-mount waterfall (G50 kept that prefetch
 * when it deleted the BFF proxy). A static SPA has no server to do it in.
 *
 * Nothing breaks, because the fallback already exists and is already used: `UsePlatformRuntime`
 * treats `undefined` -- as distinct from `[]` -- as "not prefetched, fetch it yourself", and
 * `agents` has always taken that path. The cost is one client round trip on first paint.
 *
 * Most of it comes back, from a different direction: TanStack Query's cache means a *return*
 * visit to this route paints from cache while it revalidates, which the server prefetch never
 * did. Firing those three in parallel at app boot -- rather than on mount, one component at a
 * time -- is the remaining half, and belongs with the component port rather than here.
 */
export const Route = createFileRoute("/")({
  component: ChatRoute,
});

function ChatRoute() {
  return (
    <RequireAuth>
      <ChatPage />
    </RequireAuth>
  );
}

function ChatPage() {
  const { user, hasAdminAccess } = useAuth();

  return (
    <Chat
      userLabel={user?.email ?? user?.name ?? "unknown"}
      hasAdminAccess={hasAdminAccess}
      signOutAction={signOut}
    />
  );
}
